import React, { useState } from 'react';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';
import { doc, updateDoc, arrayUnion, Timestamp, setDoc } from 'firebase/firestore';
import { db } from '../../src/firebase';

const OrganizationSetupModal: React.FC = () => {
    const { user, license, systemSettings } = useFirebaseAuth();
    const [orgName, setOrgName] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // If no user or already has organizations, don't render
    const hasOrgs = license?.organizations && license.organizations.length > 0;
    const registrationDisabled = !systemSettings.trialModeEnabled && (license?.status !== 'active');
    const isLicenseOwner = license?.id === user?.uid;

    if (!user || !license || hasOrgs || registrationDisabled || !isLicenseOwner) {
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!orgName.trim()) return;

        setLoading(true);
        setError(null);

        try {
            const licenseRef = doc(db, 'licenses', user.uid);
            await updateDoc(licenseRef, {
                orgName: orgName.trim(),
                organizations: arrayUnion(orgName.trim()),
                emailNotifications: emailNotifications, // Save the preference
                updatedAt: Timestamp.now()
            });

            // CREATE MEMBER RECORD: The first user (license holder) automatically becomes 'admin'
            // We use user.uid as both tenantId and memberId for the initial setup.
            const memberRef = doc(db, 'organizations', user.uid, 'members', user.uid);
            await setDoc(memberRef, {
                role: 'admin',
                status: 'active',
                accessibleOrgIds: [], // Will be populated by local sync or manually later
                displayName: user.displayName || '',
                email: user.email || '',
                updatedAt: Timestamp.now()
            }, { merge: true });

            console.log("[Auth] Successfully assigned 'admin' role to the first user:", user.uid);
            // Success! The listener in useLicense will update the state, and this modal will disappear.
        } catch (err: any) {
            console.error("Error creating organization:", err);
            setError(err.message || 'Ошибка при создании организации');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900/80 z-50 backdrop-blur-md transition-all duration-300">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-lg w-full border border-gray-100 dark:border-gray-700 transform transition-all scale-100">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white tracking-tight">
                        Давайте настроим ваше<br />рабочее пространство
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
                        Вы вошли как <span className="font-semibold text-gray-800 dark:text-gray-200">{user.email}</span>.
                        <br />
                        Укажите название вашей компании, чтобы мы подготовили для вас путевые листы.
                    </p>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-start">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="orgName" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 ml-1">
                            Название компании
                        </label>
                        <input
                            type="text"
                            id="orgName"
                            value={orgName}
                            onChange={(e) => setOrgName(e.target.value)}
                            className="w-full px-5 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-200 font-medium text-lg"
                            placeholder="Например: ООО Транс-Логистик"
                            required
                            disabled={loading}
                            autoFocus
                        />
                    </div>

                    <div className="flex items-center ml-1">
                        <input
                            id="emailNotifications"
                            type="checkbox"
                            checked={emailNotifications}
                            onChange={(e) => setEmailNotifications(e.target.checked)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 transition-colors cursor-pointer"
                        />
                        <label htmlFor="emailNotifications" className="ml-3 block text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                            Отправлять важные уведомления на мой email
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !orgName.trim()}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg shadow-blue-600/30 text-lg flex items-center justify-center"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Настройка...
                            </>
                        ) : (
                            'Начать работу'
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
                        Нажимая кнопку, вы принимаете условия использования сервиса
                    </p>
                </form>
            </div>
        </div>
    );
};

export default OrganizationSetupModal;
